import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { ApiError } from '../../../lib/api/_request.js';
import type { SpecNarrative, SpecNarrativeInput } from '../../../lib/api/spec-requirements.js';

/**
 * 本文の章。見出しと、その章が答える問いは翻訳キーで持つ
 * （module scope では hook を呼べないので、描画時に `t()` で解く）。
 * @graph-connects none
 */
const SECTION_KEYS = ['why', 'change', 'scope', 'verify'] as const;

export interface NarrativeEditorProps {
  /** 今保存されているナラティブ。無い案件では null */
  narrative: SpecNarrative | null;
  /** 保存中かどうか（連打を止める） */
  saving: boolean;
  /** 保存が失敗したときの原因。行数上限・権限はここに API のメッセージが入る */
  error: unknown;
  /** 保存する */
  onSave: (input: SpecNarrativeInput) => void;
  /** 編集をやめる */
  onCancel: () => void;
}

/** 空のナラティブ（まだ書かれていない案件で使う）。 @graph-connects none */
const EMPTY: SpecNarrativeInput = { why: '', change: '', scope: '', verify: '', open: [] };

/**
 * 保存できなかった理由を人が読む形にする。API が返したメッセージはそのまま見せる
 * （入口で訳し直すと、API 側で文言が変わったときに黙ってずれる）。
 * @graph-connects none
 */
function reason(t: TFunction, error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 403) return t('spec.error.forbidden');
    return error.message;
  }
  if (error instanceof Error) return error.message;
  return t('spec.error.saveFailed');
}

/**
 * ナラティブを編集する。
 *
 * 行数の上限は product-api が判定する。画面に上限の数を持たせると、変わったときに黙って
 * ずれるので持たない——代わりに**今の行数だけ**を出し、超過は保存時に API のメッセージを見せる。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business ナラティブ（なぜ・何が変わる・どこまで・どう確かめる・未決）を編集して保存する
 * @graph-connects ApiError [references] 保存が失敗した理由の判別
 */
export function NarrativeEditor({
  narrative,
  saving,
  error,
  onSave,
  onCancel,
}: NarrativeEditorProps) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<SpecNarrativeInput>(() =>
    narrative === null
      ? EMPTY
      : {
          why: narrative.why,
          change: narrative.change,
          scope: narrative.scope,
          verify: narrative.verify,
          open: narrative.open,
        },
  );

  const setOpenItem = (index: number, value: string) => {
    setDraft((d) => ({ ...d, open: d.open.map((o, i) => (i === index ? value : o)) }));
  };

  return (
    <form
      className="mt-3"
      onSubmit={(e) => {
        e.preventDefault();
        // 空行だけの未決は保存しない（API は項目数で数えるので、空項目が上限を食う）
        onSave({ ...draft, open: draft.open.map((o) => o.trim()).filter((o) => o !== '') });
      }}
    >
      <div className="space-y-4">
        {SECTION_KEYS.map((key) => (
          <label key={key} className="block">
            <span className="text-xs font-semibold text-[var(--color-text-secondary)]">
              {t(`spec.narrative.sections.${key}.heading`)}
            </span>
            <span className="text-xs text-[var(--color-text-muted)] ml-2">
              {t(`spec.narrative.sections.${key}.hint`)}
            </span>
            <textarea
              className="mt-1 block w-full border border-[var(--color-border)] rounded px-3 py-2 text-sm font-sans"
              rows={3}
              value={draft[key]}
              aria-label={t(`spec.narrative.sections.${key}.heading`)}
              onChange={(e) => {
                setDraft((d) => ({ ...d, [key]: e.target.value }));
              }}
            />
            <span className="text-xs text-[var(--color-text-muted)]">
              {t('spec.lineCount', { n: draft[key] === '' ? 0 : draft[key].split('\n').length })}
            </span>
          </label>
        ))}
      </div>

      <fieldset className="mt-4 border-l-4 border-[var(--color-warning)] bg-[var(--color-warning-muted)] px-4 py-3 rounded-r">
        <legend className="text-xs font-semibold text-[var(--color-warning-text)]">
          {t('spec.narrative.openItems.legend')}
        </legend>
        <div className="space-y-2 mt-1">
          {draft.open.map((item, index) => (
            // 並べ替えではなく入れ替えなので、index を鍵にしてよい（同じ文が 2 つ並ぶこともある）
            <div key={index} className="flex gap-2">
              <input
                className="flex-1 border border-[var(--color-warning)] rounded px-3 py-2 text-sm bg-[var(--color-bg-surface)]"
                value={item}
                aria-label={t('spec.narrative.openItems.itemAria', { index: index + 1 })}
                onChange={(e) => {
                  setOpenItem(index, e.target.value);
                }}
              />
              <button
                type="button"
                className="text-xs px-3 border border-[var(--color-warning)] rounded bg-[var(--color-bg-elevated)] text-[var(--color-warning-text)] cursor-pointer disabled:cursor-not-allowed"
                onClick={() => {
                  setDraft((d) => ({ ...d, open: d.open.filter((_, i) => i !== index) }));
                }}
              >
                {t('spec.action.remove')}
              </button>
            </div>
          ))}
        </div>
        <button
          type="button"
          className="mt-2 text-xs px-3 py-1 border border-[var(--color-warning)] rounded bg-[var(--color-bg-elevated)] text-[var(--color-warning-text)] cursor-pointer disabled:cursor-not-allowed"
          onClick={() => {
            setDraft((d) => ({ ...d, open: [...d.open, ''] }));
          }}
        >
          {t('spec.narrative.openItems.add')}
        </button>
      </fieldset>

      {error === null || error === undefined ? null : (
        <p role="alert" className="mt-3 text-sm text-[var(--color-error)] whitespace-pre-wrap">
          {reason(t, error)}
        </p>
      )}

      <div className="mt-4 flex gap-2">
        <button
          type="submit"
          disabled={saving}
          className="text-sm px-4 py-2 rounded bg-[var(--color-accent-primary)] text-white disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed"
        >
          {t(saving ? 'spec.action.saving' : 'spec.action.save')}
        </button>
        <button
          type="button"
          className="text-sm px-4 py-2 rounded border border-[var(--color-border)] bg-[var(--color-bg-elevated)] cursor-pointer disabled:cursor-not-allowed"
          onClick={onCancel}
        >
          {t('spec.action.cancel')}
        </button>
      </div>
    </form>
  );
}
